import {
  useEffect,
  useState,
  type ComponentType,
  type HTMLAttributes,
} from "react";

import { InlineError, Spinner } from "./Feedback";

export interface MarkdownProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> {
  children: string;
}

type MarkdownRendererComponent = ComponentType<{ children: string }>;

function withMarkdownClass(className: string | undefined): string {
  return className ? `marker-markdown ${className}` : "marker-markdown";
}

function markdownErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Markdown unavailable.";
}

export function Markdown({ children, className, ...props }: MarkdownProps) {
  const [Renderer, setRenderer] = useState<MarkdownRendererComponent | null>(
    null,
  );
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // react-markdown is funnelled through a single dynamic import so it lands
    // in its own lazy chunk (mirrors the AudioPlayer/wavesurfer and
    // CodeEditor/codeEditorModule precedents) rather than the always-loaded
    // main app bundle.
    void import("./markdownModule")
      .then((module) => {
        if (!cancelled) {
          setRenderer(() => module.MarkdownRenderer);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(markdownErrorMessage(error));
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      aria-busy={!Renderer && !loadError}
      className={withMarkdownClass(className)}
      {...props}
    >
      {Renderer ? (
        <Renderer>{children}</Renderer>
      ) : loadError ? (
        <InlineError>{loadError}</InlineError>
      ) : (
        <span className="marker-markdown__loading">
          <Spinner label="Loading content" />
        </span>
      )}
    </div>
  );
}
